import { ALL_TRIPS_TRIP_ID } from 'src/routes/trip-analyzer/data/trip-analyzer.constants';
import { isAllTrips, normalizeTripIds } from 'src/routes/trip-analyzer/data/tripSelection';
import { getInitialTripAnalyzerState } from 'src/routes/trip-analyzer/data/trip-analyzer.actions';
import { catalogKinds, types } from 'src/routes/trip-analyzer/data/trip-analyzer.types';

const clearCatalog = (catalog, kinds) => {
    const nextCatalog = { ...catalog };

    kinds.forEach((kind) => {
        nextCatalog[kind] = {
            items: [],
            loading: false,
            error: null,
        };
    });

    return nextCatalog;
};

const clearTimeline = (timeline) => ({
    ...timeline,
    data: null,
    loading: false,
    error: null,
    message: null,
});

const applyQueryCascade = (state, nextQuery) => {
    const previousQuery = state.query;
    let nextState = {
        ...state,
        query: {
            ...state.query,
            ...nextQuery,
        },
    };

    if (
        nextQuery.serviceDate !== undefined
        && nextQuery.serviceDate !== previousQuery.serviceDate
    ) {
        nextState = {
            ...nextState,
            catalog: clearCatalog(nextState.catalog, [
                catalogKinds.ROUTES,
                catalogKinds.DIRECTIONS,
                catalogKinds.TRIPS,
            ]),
            columns: {
                items: [],
                loading: false,
                error: null,
                cacheKey: null,
            },
            timeline: clearTimeline(nextState.timeline),
            query: {
                ...nextState.query,
                routeKey: null,
                directionId: null,
                observedTripIds: [ALL_TRIPS_TRIP_ID],
                compareServiceDate: null,
                compareObservedTripId: null,
                partnerKeys: [],
            },
        };
    }

    if (
        nextQuery.routeKey !== undefined
        && nextQuery.routeKey !== previousQuery.routeKey
    ) {
        nextState = {
            ...nextState,
            catalog: clearCatalog(nextState.catalog, [
                catalogKinds.DIRECTIONS,
                catalogKinds.TRIPS,
            ]),
            columns: {
                items: [],
                loading: false,
                error: null,
                cacheKey: null,
            },
            timeline: clearTimeline(nextState.timeline),
            query: {
                ...nextState.query,
                directionId: null,
                observedTripIds: [ALL_TRIPS_TRIP_ID],
                partnerKeys: [],
            },
        };
    }

    if (
        nextQuery.directionId !== undefined
        && nextQuery.directionId !== previousQuery.directionId
    ) {
        nextState = {
            ...nextState,
            catalog: clearCatalog(nextState.catalog, [catalogKinds.TRIPS]),
            columns: {
                items: [],
                loading: false,
                error: null,
                cacheKey: null,
            },
            timeline: clearTimeline(nextState.timeline),
            query: {
                ...nextState.query,
                observedTripIds: [ALL_TRIPS_TRIP_ID],
                partnerKeys: [],
            },
        };
    }

    return nextState;
};

const tripAnalyzerReducer = (state = getInitialTripAnalyzerState(), action) => {
    switch (action.type) {
        case types.RESET_TRIP_ANALYZER:
            return getInitialTripAnalyzerState();

        case types.SET_QUERY:
            return applyQueryCascade(state, action.payload);

        case types.SET_COMPARE_ENABLED:
            return applyQueryCascade(state, {
                compareEnabled: action.payload,
                compareServiceDate: action.payload ? state.query.compareServiceDate : null,
                compareObservedTripId: action.payload ? state.query.compareObservedTripId : null,
                observedTripIds: action.payload && isAllTrips(state.query.observedTripIds)
                    ? []
                    : (normalizeTripIds(state.query.observedTripIds).length > 1
                        ? normalizeTripIds(state.query.observedTripIds).slice(0, 1)
                        : state.query.observedTripIds),
            });

        case types.SET_TRIP_ORDER:
            return {
                ...state,
                tripOrderByCorridor: {
                    ...state.tripOrderByCorridor,
                    [action.payload.corridorKey]: action.payload.orderedTripIds,
                },
            };

        case types.OPEN_TSP_PANEL:
            return {
                ...state,
                tspPanel: {
                    open: true,
                    loading: false,
                    error: null,
                    marker: action.payload,
                    sessions: [],
                    events: [],
                },
            };

        case types.CLOSE_TSP_PANEL:
            return {
                ...state,
                tspPanel: {
                    open: false,
                    loading: false,
                    error: null,
                    marker: null,
                    sessions: [],
                    events: [],
                },
            };

        case types.SERVICE_DATES_REQUEST:
            return {
                ...state,
                serviceDates: {
                    ...state.serviceDates,
                    loading: true,
                    error: null,
                },
            };

        case types.SERVICE_DATES_SUCCESS:
            return {
                ...state,
                serviceDates: {
                    ...state.serviceDates,
                    loading: false,
                    error: null,
                    availableDates: action.payload.availableDates ?? state.serviceDates.availableDates,
                    minDate: action.payload.minDate ?? state.serviceDates.minDate,
                    maxDate: action.payload.maxDate ?? state.serviceDates.maxDate,
                },
            };

        case types.SERVICE_DATES_ERROR:
            return {
                ...state,
                serviceDates: {
                    ...state.serviceDates,
                    loading: false,
                    error: action.payload,
                },
            };

        case types.CATALOG_REQUEST:
            return {
                ...state,
                catalog: {
                    ...state.catalog,
                    [action.payload.kind]: {
                        ...state.catalog[action.payload.kind],
                        loading: true,
                        error: null,
                    },
                },
            };

        case types.CATALOG_SUCCESS:
            return {
                ...state,
                catalog: {
                    ...state.catalog,
                    [action.payload.kind]: {
                        items: action.payload.items ?? [],
                        loading: false,
                        error: null,
                    },
                    ...(action.payload.partners ? { partners: action.payload.partners } : {}),
                },
            };

        case types.CATALOG_ERROR:
            return {
                ...state,
                catalog: {
                    ...state.catalog,
                    [action.payload.kind]: {
                        ...state.catalog[action.payload.kind],
                        loading: false,
                        error: action.payload.error,
                    },
                },
            };

        case types.COLUMNS_REQUEST:
            return {
                ...state,
                columns: {
                    ...state.columns,
                    loading: true,
                    error: null,
                },
            };

        case types.COLUMNS_SUCCESS:
            return {
                ...state,
                columns: {
                    items: action.payload.items ?? [],
                    loading: false,
                    error: null,
                    cacheKey: action.payload.cacheKey ?? null,
                },
                catalog: {
                    ...state.catalog,
                    partners: action.payload.partners ?? state.catalog.partners,
                },
            };

        case types.COLUMNS_ERROR:
            return {
                ...state,
                columns: {
                    ...state.columns,
                    loading: false,
                    error: action.payload,
                },
            };

        case types.TIMELINE_REQUEST:
            return {
                ...state,
                timeline: {
                    ...state.timeline,
                    loading: true,
                    error: null,
                    message: null,
                },
            };

        case types.TIMELINE_SUCCESS:
            return {
                ...state,
                timeline: {
                    data: action.payload,
                    loading: false,
                    error: null,
                    message: action.payload?.trips?.length ? null : 'No trips found for the selected filters.',
                },
            };

        case types.TIMELINE_ERROR:
            return {
                ...state,
                timeline: {
                    ...state.timeline,
                    loading: false,
                    error: action.payload,
                    message: action.payload || 'Unable to load timeline.',
                },
            };

        case types.TSP_EVENTS_REQUEST:
            return {
                ...state,
                tspPanel: {
                    ...state.tspPanel,
                    loading: true,
                    error: null,
                },
            };

        case types.TSP_EVENTS_SUCCESS:
            return {
                ...state,
                tspPanel: {
                    ...state.tspPanel,
                    loading: false,
                    error: null,
                    sessions: action.payload.sessions ?? [],
                    events: action.payload.events ?? [],
                },
            };

        case types.TSP_EVENTS_ERROR:
            return {
                ...state,
                tspPanel: {
                    ...state.tspPanel,
                    loading: false,
                    error: action.payload,
                },
            };

        default:
            return state;
    }
};

export default tripAnalyzerReducer;
